'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { QRCodeCanvas } from 'qrcode.react';
import {
  OrderStatusEnum,
  type DBOrder,
  type Order,
  type OrderStatus,
  type Product,
  type ReturnedContainer,
} from '../lib/types';
import { Trash2, Plus, History, AlertCircle } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { ScrollArea } from 'components/ui/scroll-area';
import { useProducts } from '../hooks/use-products';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { OrderHistoryDialog } from './order-history-dialog';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Separator } from 'components/ui/separator';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { productUnitsMap } from '../lib/productUnits';
import { toast } from 'sonner';
import firebase from 'firebase/compat/app';
import { useAppSelector } from 'hooks';
import { getStatusColor, translateStatus } from '../lib/utils';
import { isAks2talOnly, isAdmin, aks2talUsers, isAdminAndMichael } from 'components/AdminOnly';
import { Textarea } from 'components/ui/textarea';
import { containerTypes, ContainerTypeEnum } from '../lib/types';
import OrderDealButton from './order-deal';
import { distributionStorages } from './create-order-dialog';

interface OrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  mode: 'view' | 'edit';
}

interface ValidationErrors {
  companyName?: string;
  deliveryDate?: string;
  deliveryAddress?: string;
  deliveryZip?: string;
  deliveryCity?: string;
  deliveryAtt?: string;
  orderRef?: string;
  terms?: string;
  invoiceEmail?: string;
  nextExpectedDeliveryDate?: string;
  internalNote?: string;
  contactPerson?: string;
  products?: string;
}

/**
 * Renders an order dialog for viewing or editing order details.
 *
 * @param {OrderDialogProps} props - The properties for the dialog.
 * @param {boolean} props.open - Controls whether the dialog is open.
 * @param {function} props.onOpenChange - Callback to handle dialog open state changes.
 * @param {Order|null} props.order - The order data to be displayed or edited.
 * @param {'view'|'edit'} props.mode - The mode of the dialog, either 'view' or 'edit'.
 *
 * @returns A dialog component for displaying order details, including products,
 *          order information, and delivery note. Supports adding and removing
 *          products and containers, and provides validation for the form.
 */
export function OrderDialog({ open, onOpenChange, order, mode }: OrderDialogProps) {
  const { t } = useTranslation();
  const userId = useAppSelector((state) => state.user.details.uid);
  const isAks = isAks2talOnly(userId);
  const isUserAdmin = isAdmin(userId);
  const isUserMichael = isAdminAndMichael(userId);

  const [formData, setFormData] = useState<Order>({
    id: '',
    orgId: '',
    companyName: '',
    products: [],
    status: OrderStatusEnum.AWAITING,
    deliveryAddress: '',
    deliveryCity: '',
    deliveryZip: '',
    deliveryDate: new Date(),
    nextExpectedDeliveryDate: undefined,
    orderRef: '',
    terms: '',
    internalNote: '',
    invoiceEmail: '',
    invoiceNo: '',
    eanNumber: '',
    cvrNumber: '',
    deliveryAtt: '',
    contactPerson: '',
    createdBy: { id: '', displayName: '' },
    createTime: new Date(),
    returnedContainers: [],
  });

  const { products: availableProducts, loadingProducts } = useProducts(formData.orgId);
  const [activeTab, setActiveTab] = useState('details');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Effect for handling view/edit mode
  useEffect(() => {
    if (order && open) {
      console.log({ order });

      setFormData(order);
      setActiveTab('details');
      setErrors({});
      setTouched({});
      setFormSubmitted(false);
    }
  }, [order, open]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = t('orders.companyNameRequired');
    }

    if (!formData.deliveryDate) {
      newErrors.deliveryDate = t('orders.deliveryDateRequired');
    }
    if (!formData.nextExpectedDeliveryDate) {
      newErrors.nextExpectedDeliveryDate = t('orders.nextExpectedDeliveryDateRequired');
    }

    if (!formData.deliveryAddress?.trim()) {
      newErrors.deliveryAddress = t('orders.deliveryAddressRequired');
    }
    if (!formData.deliveryZip?.trim()) {
      newErrors.deliveryZip = t('orders.deliveryZipRequired');
    }
    if (!formData.deliveryCity?.trim()) {
      newErrors.deliveryCity = t('orders.deliveryCityRequired');
    }

    if (!formData.deliveryAtt?.trim()) {
      newErrors.deliveryAtt = t('orders.deliveryAttRequired');
    }

    if (!formData.contactPerson?.trim()) {
      newErrors.contactPerson = t('orders.contactPersonRequired');
    }
    if (!formData.orderRef?.trim()) {
      newErrors.orderRef = t('orders.orderRefRequired');
    }
    if (!formData.invoiceEmail?.trim()) {
      newErrors.invoiceEmail = t('orders.invoiceEmailRequired');
    }

    if (formData.products.length === 0) {
      newErrors.products = t('orders.productsRequired');
    }
    if (
      formData.products.some(
        (product) => !product.quantity || product.quantity <= 0 || !product.unit
      )
    ) {
      newErrors.products = t('orders.productsQuantityRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error(Object.values(newErrors).join(', '));
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof Order, value: string | Date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    // Clear error for this field if it exists
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleStatusChange = (value: OrderStatus) => {
    setFormData((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const handleAddProduct = () => {
    if (availableProducts.length === 0) return;

    const firstProduct = availableProducts[0];
    const newProduct = {
      id: firstProduct.id,
      name: firstProduct.title,
      quantity: 1,
      unit: undefined, // firstProduct.defaultUnit,
    };

    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, newProduct],
    }));

    // Clear products error if it exists
    if (errors.products) {
      setErrors((prev) => ({
        ...prev,
        products: undefined,
      }));
    }
  };

  const handleRemoveProduct = (index: number) => {
    setFormData((prev) => {
      const newProducts = [...prev.products];
      newProducts.splice(index, 1);

      // If removing the last product, set the products error
      if (newProducts.length === 0) {
        setErrors((prev) => ({
          ...prev,
          products: t('orders.productsRequired'),
        }));
      }

      return {
        ...prev,
        products: newProducts,
      };
    });
  };

  const handleProductChange = (index: number, field: keyof Product, value: string | number) => {
    setFormData((prev) => {
      const newProducts = [...prev.products];
      newProducts[index] = { ...newProducts[index], [field]: value };
      return {
        ...prev,
        products: newProducts,
      };
    });
  };

  const handleAddReturnedContainer = () => {
    const newContainer: ReturnedContainer = {
      quantity: 1,
      type: ContainerTypeEnum.LITER_1000,
    };
    setFormData((prev) => ({
      ...prev,
      returnedContainers: [...(prev.returnedContainers || []), newContainer],
    }));
  };

  const handleRemoveReturnedContainer = (index: number) => {
    setFormData((prev) => {
      const newContainers = [...(prev.returnedContainers || [])];
      newContainers.splice(index, 1);
      return {
        ...prev,
        returnedContainers: newContainers,
      };
    });
  };

  const handleReturnedContainerChange = (
    index: number,
    field: keyof ReturnedContainer,
    value: string | number
  ) => {
    setFormData((prev) => {
      const newContainers = [...(prev.returnedContainers || [])];
      // @ts-ignore
      newContainers[index] = { ...newContainers[index], [field]: value };
      return {
        ...prev,
        returnedContainers: newContainers,
      };
    });
  };

  const getProductUnits = (productId: string): { value: number; unit: string; label: string }[] => {
    const product = availableProducts.find((p) => p.id === productId);
    // @ts-ignore
    return product?.id ? productUnitsMap[product.id] : []; //product?.availableUnits ? product.availableUnits : [];
  };

  async function onSave(order: Order) {
    try {
      if (order.productDataIncomplete) {
        throw new Error('Produktdata er ufuldstændige. Ordren skal kontrolleres før den kan gemmes.');
      }
      console.log('Saving order', order);
      if (!order.id) {
        throw new Error('Order ID is missing');
      }
      await firebase
        .firestore()
        .collection('orders')
        .doc(order.id)
        .set(
          {
            companyName: order.companyName,
            products: order.products,
            delivery: {
              date: order.deliveryDate,
              address: order.deliveryAddress,
              zip: order.deliveryZip,
              city: order.deliveryCity,
              att: order.deliveryAtt,
              nextExpectedDeliveryDate: order.nextExpectedDeliveryDate,
            },
            orderRef: order.orderRef,
            terms: order.terms,
            invoiceEmail: order.invoiceEmail,
            eanNumber: order.eanNumber,
            cvrNumber: order.cvrNumber,
            internalNote: order.internalNote,
            updateTime: new Date(),
            status: order.status,
            contact: { name: order.contactPerson },
            createdBy: order.createdBy,
            returnedContainers: order.returnedContainers || [],
            ...(order.intermediateStorage !== undefined && {
              intermediateStorage: order.intermediateStorage,
            }),
            ...(order.invoiceNo !== undefined && { invoiceNo: order.invoiceNo }),
          } as Omit<DBOrder, 'id'>,
          { merge: true }
        );
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving order', error);
    } finally {
      setFormSubmitted(false);
    }
  }

  const handleSave = () => {
    const isValid = validateForm();
    if (!isValid) return;
    setFormSubmitted(true);

    onSave(formData);
  };

  const isReadOnly = mode === 'view';
  const isRequired = mode === 'edit';

  // Helper to determine if a field has an error
  const hasError = (field: keyof ValidationErrors): boolean => {
    return !!(formSubmitted || touched[field]) && !!errors[field];
  };

  // Format date with Danish locale
  const formatDate = (date: Date | string) => {
    return format(new Date(date), 'PPP', { locale: da });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('orders.copiedToClipboard'));
  };

  const productsReadonly = isReadOnly || order?.status !== OrderStatusEnum.AWAITING;
  const readOnlyCls = isReadOnly || !isAks ? ' bg-muted cursor-copy' : '';
  const readOnlyProductCls = productsReadonly ? ' bg-muted cursor-copy' : '';

  const distributionStorageName = distributionStorages.find(
    (storage) => storage.id === formData.intermediateStorage
  )?.name;
  // Render the details tab
  const renderDetailsTab = () => {
    return (
      <div className="space-y-6 py-4">
        {/* Products Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex">
              {t('orders.products')}{' '}
              {isRequired && <span className="text-destructive ml-1">*</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingProducts ? (
              <div className="text-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-muted-foreground">{t('orders.loadingProducts')}</p>
              </div>
            ) : formData.products.length === 0 ? (
              <div
                className={`text-center py-8 text-muted-foreground border rounded-md ${
                  errors.products ? 'border-destructive' : ''
                }`}
              >
                {t('orders.noProductsAdded')}
              </div>
            ) : (
              formData.products.map((product, index) => {
                // @ts-ignore
                const unit = productUnitsMap[product.id]?.find((p: any) => p.value == product.unit);

                return (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end  rounded-md">
                    <div className={`${productsReadonly ? 'col-span-7' : 'col-span-6'} space-y-2`}>
                      {index === 0 && (
                        <Label htmlFor={`product-${index}`}>{t('orders.product')}</Label>
                      )}
                      {productsReadonly ? (
                        <Input
                          id={`product-${index}`}
                          value={product.name}
                          readOnly
                          className={readOnlyProductCls}
                        />
                      ) : (
                        <Select
                          value={product.id}
                          onValueChange={(value) => {
                            const selectedProduct = availableProducts.find((p) => p.id === value);
                            if (selectedProduct) {
                              handleProductChange(index, 'id', value);
                              handleProductChange(index, 'name', selectedProduct.title);
                              // handleProductChange(index, 'unit', selectedProduct.defaultUnit);
                            }
                          }}
                        >
                          <SelectTrigger id={`product-${index}`}>
                            <SelectValue placeholder={t('orders.selectProduct')} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableProducts.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="col-span-2 space-y-2">
                      {index === 0 && (
                        <Label htmlFor={`quantity-${index}`}>{t('orders.quantity')}</Label>
                      )}
                      <Input
                        id={`quantity-${index}`}
                        type="number"
                        value={product.quantity}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            'quantity',
                            Number.parseInt(e.target.value) || 0
                          )
                        }
                        readOnly={productsReadonly}
                        className={readOnlyProductCls}
                      />
                    </div>
                    <div className="col-span-3 space-y-2">
                      {index === 0 && <Label htmlFor={`unit-${index}`}>{t('orders.unit')}</Label>}
                      {isReadOnly || productsReadonly ? (
                        <Input
                          id={`unit-${index}`}
                          value={unit?.label}
                          readOnly
                          className={readOnlyProductCls}
                        />
                      ) : (
                        <Select
                          value={product.unit}
                          onValueChange={(value) => handleProductChange(index, 'unit', value)}
                        >
                          <SelectTrigger id={`unit-${index}`}>
                            <SelectValue placeholder={t('orders.selectUnit')} />
                          </SelectTrigger>
                          <SelectContent>
                            {getProductUnits(product.id).map((unit) => (
                              <SelectItem key={unit.value} value={'' + unit.value}>
                                {unit.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {!isReadOnly && !productsReadonly && (
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveProduct(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {errors.products && <p className="text-sm text-destructive">{errors.products}</p>}

            {!isReadOnly && !productsReadonly && (
              <Button
                onClick={handleAddProduct}
                className="w-full"
                disabled={
                  loadingProducts ||
                  availableProducts.length === 0 ||
                  availableProducts[0].id.startsWith('DEFAULT')
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('orders.addProduct')}
              </Button>
            )}
          </CardContent>
        </Card>
        {/* Admin Only - Is intermediate storage */}
        {isUserMichael && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Distributionslager
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Select
                  disabled
                  value={formData.intermediateStorage}
                  onValueChange={(value) => {
                    const selectedStorage = distributionStorages.find(
                      (storage) => storage.id === value
                    );
                    console.log({ selectedStorage, formData });
                    if (selectedStorage) {
                      setFormData((prev) => ({
                        ...prev,
                        intermediateStorage: value,
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Vælg lager">
                      {distributionStorageName || 'Vælg lager'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {distributionStorages.map((storage) => (
                      <SelectItem key={storage.id} value={storage.id}>
                        {storage.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Order Information Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.orderInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName" className="flex">
                  {t('orders.companyName')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="companyName"
                  onClick={() => isReadOnly && copyToClipboard(formData.companyName)}
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.companyName ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.companyName && (
                  <p className="text-sm text-destructive">{errors.companyName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="flex">
                  {t('orders.status')}
                </Label>
                {isReadOnly || !isAks || order?.status === OrderStatusEnum.COMPLETED ? (
                  <div className="h-9 flex items-center px-3 py-2 border rounded-md bg-muted text-sm">
                    <Badge
                      variant="outline"
                      className={getStatusColor(formData.status) + ' w-2 h-2 p-0 mr-2'}
                    />
                    {t(translateStatus(formData.status))}
                  </div>
                ) : (
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleStatusChange(value as OrderStatus)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder={t('orders.selectOption')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={OrderStatusEnum.AWAITING}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-yellow-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.AWAITING))}
                        </div>
                      </SelectItem>
                      <SelectItem value={OrderStatusEnum.PROCESSING}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-blue-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.PROCESSING))}
                        </div>
                      </SelectItem>
                      <SelectItem value={OrderStatusEnum.READY_FOR_SHIPPING}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-purple-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.READY_FOR_SHIPPING))}
                        </div>
                      </SelectItem>
                      <SelectItem value={OrderStatusEnum.IN_TRANSIT}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-purple-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.IN_TRANSIT))}
                        </div>
                      </SelectItem>
                      <SelectItem value={OrderStatusEnum.AWAITING_INVOICE}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-cyan-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.AWAITING_INVOICE))}
                        </div>
                      </SelectItem>
                      <SelectItem value={OrderStatusEnum.COMPLETED}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-green-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.COMPLETED))}
                        </div>
                      </SelectItem>
                      <SelectItem value={OrderStatusEnum.VOIDED}>
                        <div className="flex items-center">
                          <Badge variant="outline" className="bg-red-500 w-2 h-2 p-0 mr-2" />
                          {t(translateStatus(OrderStatusEnum.VOIDED))}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="createdBy">{t('orders.createdBy')}</Label>
                {isUserAdmin && mode === 'edit' ? (
                  <Select
                    value={formData.createdBy.id}
                    onValueChange={(value) => {
                      const selectedUser = aks2talUsers.find((user) => user.id === value);
                      if (selectedUser) {
                        setFormData((prev) => ({
                          ...prev,
                          createdBy: {
                            id: value,
                            displayName: selectedUser.displayName,
                          },
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Vælg bruger">
                        {formData.createdBy.displayName || 'Vælg bruger'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {aks2talUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="createdBy"
                    value={formData.createdBy.displayName}
                    readOnly
                    className={readOnlyCls}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="createdAt">{t('orders.createdAt')}</Label>
                <Input
                  id="createdAt"
                  value={formatDate(formData.createTime)}
                  readOnly
                  className={readOnlyCls}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Returned Containers Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.returnedContainers', 'Retur emballage')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {formData.returnedContainers?.map((container, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end rounded-md">
                  <div className="col-span-5 space-y-2">
                    {index === 0 && <Label>{t('orders.containerType', 'Emballage type')}</Label>}
                    <Select
                      value={container.type}
                      onValueChange={(value) => handleReturnedContainerChange(index, 'type', value)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('orders.selectContainerType', 'Vælg emballage type')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {containerTypes.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-5 space-y-2">
                    {index === 0 && <Label>{t('orders.quantity', 'Antal')}</Label>}
                    <Input
                      type="number"
                      value={container.quantity}
                      onChange={(e) =>
                        handleReturnedContainerChange(
                          index,
                          'quantity',
                          parseInt(e.target.value, 10) || 0
                        )
                      }
                      disabled={isReadOnly}
                      min={1}
                    />
                  </div>
                  {!isReadOnly && (
                    <div className="col-span-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveReturnedContainer(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {!isReadOnly && (
                <Button variant="outline" onClick={handleAddReturnedContainer}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('orders.addContainer', 'Tilføj emballage')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delivery Information Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('orders.deliveryInformation')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className="space-y-2"
              role="button"
              onClick={() =>
                isReadOnly && formData.deliveryAddress && copyToClipboard(formData.deliveryAddress)
              }
            >
              <Label htmlFor="deliveryAddress" className="flex">
                {t('orders.deliveryAddress')}{' '}
                {isRequired && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Input
                id="deliveryAddress"
                value={formData.deliveryAddress}
                onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                readOnly={isReadOnly}
                placeholder={t('orders.enterDeliveryAddress')}
                className={errors.deliveryAddress ? 'border-destructive' : '' + readOnlyCls}
              />
              {errors.deliveryAddress && (
                <p className="text-sm text-destructive">{errors.deliveryAddress}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.deliveryZip && copyToClipboard(formData.deliveryZip)
                }
              >
                <Label htmlFor="deliveryZip" className="flex">
                  {t('orders.deliveryZip')} <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="deliveryZip"
                  value={formData.deliveryZip}
                  onChange={(e) => handleInputChange('deliveryZip', e.target.value)}
                  readOnly={isReadOnly}
                  placeholder={t('orders.enterDeliveryZip')}
                  className={errors.deliveryZip ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.deliveryZip && (
                  <p className="text-sm text-destructive">{errors.deliveryZip}</p>
                )}
              </div>
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.deliveryCity && copyToClipboard(formData.deliveryCity)
                }
              >
                <Label htmlFor="deliveryCity" className="flex">
                  {t('orders.deliveryCity')} <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="deliveryCity"
                  value={formData.deliveryCity}
                  onChange={(e) => handleInputChange('deliveryCity', e.target.value)}
                  readOnly={isReadOnly}
                  placeholder={t('orders.enterDeliveryCity')}
                  className={errors.deliveryCity ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.deliveryCity && (
                  <p className="text-sm text-destructive">{errors.deliveryCity}</p>
                )}
              </div>
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly &&
                  formData.deliveryDate &&
                  copyToClipboard(formData.deliveryDate.toISOString())
                }
              >
                <Label htmlFor="deliveryDate" className="flex">
                  {t('orders.deliveryDate')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                {isReadOnly ? (
                  <div
                    className={
                      'h-9 flex items-center px-3 py-2 border rounded-md bg-background text-sm' +
                      readOnlyCls
                    }
                  >
                    {formData.deliveryDate
                      ? formatDate(formData.deliveryDate)
                      : t('orders.notSpecified')}
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="deliveryDate"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !formData.deliveryDate && 'text-muted-foreground',
                          errors.deliveryDate && 'border-destructive'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.deliveryDate ? (
                          formatDate(formData.deliveryDate)
                        ) : (
                          <span>{t('orders.pickDate')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={
                          formData.deliveryDate ? new Date(formData.deliveryDate) : undefined
                        }
                        onSelect={(date) => {
                          if (date) {
                            handleInputChange('deliveryDate', date);
                          }
                        }}
                        initialFocus
                        locale={da}
                      />
                    </PopoverContent>
                  </Popover>
                )}
                {errors.deliveryDate && (
                  <p className="text-sm text-destructive">{errors.deliveryDate}</p>
                )}
              </div>
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly &&
                  formData.nextExpectedDeliveryDate &&
                  copyToClipboard(formData.nextExpectedDeliveryDate.toISOString())
                }
              >
                <Label htmlFor="nextExpectedDeliveryDate" className="flex">
                  {t('orders.nextExpectedDeliveryDate')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                {isReadOnly ? (
                  <div
                    className={
                      'h-9 flex items-center px-3 py-2 border rounded-md bg-background text-sm' +
                      readOnlyCls
                    }
                  >
                    {formData.nextExpectedDeliveryDate
                      ? formatDate(formData.nextExpectedDeliveryDate)
                      : t('orders.notSpecified')}
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="nextExpectedDeliveryDate"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !formData.deliveryDate && 'text-muted-foreground',
                          errors.nextExpectedDeliveryDate && 'border-destructive'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.nextExpectedDeliveryDate ? (
                          formatDate(formData.nextExpectedDeliveryDate)
                        ) : (
                          <span>{t('orders.pickDate')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={
                          formData.nextExpectedDeliveryDate
                            ? new Date(formData.nextExpectedDeliveryDate)
                            : undefined
                        }
                        onSelect={(date) => {
                          if (date) {
                            handleInputChange('nextExpectedDeliveryDate', date);
                          }
                        }}
                        initialFocus
                        locale={da}
                      />
                    </PopoverContent>
                  </Popover>
                )}
                {errors.nextExpectedDeliveryDate && (
                  <p className="text-sm text-destructive">{errors.nextExpectedDeliveryDate}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.deliveryAtt && copyToClipboard(formData.deliveryAtt)
                }
              >
                <Label htmlFor="deliveryAtt" className="flex">
                  {t('orders.deliveryAtt')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="deliveryAtt"
                  value={formData.deliveryAtt}
                  onChange={(e) => handleInputChange('deliveryAtt', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.deliveryAtt ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.deliveryAtt && (
                  <p className="text-sm text-destructive">{errors.deliveryAtt}</p>
                )}
              </div>
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.contactPerson && copyToClipboard(formData.contactPerson)
                }
              >
                <Label htmlFor="contactPerson" className="flex">
                  {t('orders.contactPerson')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.contactPerson ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.contactPerson && (
                  <p className="text-sm text-destructive">{errors.contactPerson}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.orderRef && copyToClipboard(formData.orderRef)
                }
              >
                <Label htmlFor="orderRef" className="flex">
                  {t('orders.orderRef')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="orderRef"
                  value={formData.orderRef}
                  onChange={(e) => handleInputChange('orderRef', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.orderRef ? 'border-destructive' : '' + readOnlyCls}
                />
                <p className="text-xs text-slate-400">Ikke relevant? (skriv "-" i feltet)</p>
                {errors.orderRef && <p className="text-sm text-destructive">{errors.orderRef}</p>}
              </div>
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.invoiceEmail && copyToClipboard(formData.invoiceEmail)
                }
              >
                <Label htmlFor="invoiceEmail" className="flex">
                  {t('orders.invoiceEmail')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="invoiceEmail"
                  value={formData.invoiceEmail || ''}
                  onChange={(e) => handleInputChange('invoiceEmail', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.invoiceEmail ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.invoiceEmail && (
                  <p className="text-sm text-destructive">{errors.invoiceEmail}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.eanNumber && copyToClipboard(formData.eanNumber)
                }
              >
                <Label htmlFor="eanNumber">{t('orders.eanNumber')}</Label>
                <Input
                  id="eanNumber"
                  value={formData.eanNumber || ''}
                  onChange={(e) => handleInputChange('eanNumber', e.target.value)}
                  readOnly={isReadOnly}
                  className={readOnlyCls}
                />
              </div>
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.cvrNumber && copyToClipboard(formData.cvrNumber)
                }
              >
                <Label htmlFor="cvrNumber">{t('orders.cvrNumber')}</Label>
                <Input
                  id="cvrNumber"
                  value={formData.cvrNumber || ''}
                  onChange={(e) => handleInputChange('cvrNumber', e.target.value)}
                  readOnly={isReadOnly}
                  className={readOnlyCls}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.invoiceNo && copyToClipboard(formData.invoiceNo)
                }
              >
                <Label htmlFor="invoiceNo" className="flex">
                  {t('orders.invoiceNo')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input
                  id="invoiceNo"
                  value={formData.invoiceNo || ''}
                  onChange={(e) => handleInputChange('invoiceNo', e.target.value)}
                  readOnly={isReadOnly}
                  className={readOnlyCls}
                />
                {/* <p className="text-xs text-slate-400">Ikke relevant? (efterlad tom)</p>
                {errors.invoiceNo && <p className="text-sm text-destructive">{errors.invoiceNo}</p>} */}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() => isReadOnly && formData.terms && copyToClipboard(formData.terms)}
              >
                <Label htmlFor="terms" className="flex">
                  {t('orders.terms')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Textarea
                  id="terms"
                  value={formData.terms}
                  onChange={(e) => handleInputChange('terms', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.terms ? 'border-destructive' : '' + readOnlyCls}
                />
                {errors.terms && <p className="text-sm text-destructive">{errors.terms}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div
                className="space-y-2"
                role="button"
                onClick={() =>
                  isReadOnly && formData.internalNote && copyToClipboard(formData.internalNote)
                }
              >
                <Label htmlFor="internalNote" className="flex">
                  {t('orders.internalNote')}{' '}
                  {isRequired && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Textarea
                  id="internalNote"
                  value={formData.internalNote}
                  onChange={(e) => handleInputChange('internalNote', e.target.value)}
                  readOnly={isReadOnly}
                  className={errors.internalNote ? 'border-destructive' : '' + readOnlyCls}
                />
                <p className="text-xs text-slate-400">Denne note er specifik til denne ordre</p>
                {errors.internalNote && (
                  <p className="text-sm text-destructive">{errors.internalNote}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render the delivery note tab
  const renderDeliveryNoteTab = () => {
    return (
      <div className="space-y-6 py-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">{t('orders.deliveryNote')}</h2>
          <Button
            onClick={() => {
              const printWindow = window.open('', '_blank');
              printWindow?.document.write(`
                <html>
                  <head>
                    <title>${t('orders.deliveryNote')} - ${formData.id}</title>
                    <style>
                      body { font-family: system-ui, sans-serif; line-height: 1.5; padding: 20px; }
                      .container { max-width: 800px; margin: 0 auto; }
                      .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
                      .qr-code { text-align: right; }
                      table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                      th { background-color: #f2f2f2; }
                      .footer { margin-top: 40px; display: flex; justify-content: space-between; }
                      .signature-box { border-top: 1px solid #000; width: 200px; padding-top: 5px; }
                      @media print {
                        button { display: none; }
                      }
                    </style>
                  </head>
                  <body>
                    <div class="container">
                      <div class="header">
                        <div>
                          <h1>${t('orders.deliveryNote')}</h1>
                          <p>${t('orders.orderId')}: ${formData.id}</p>
                          <p>${t('Ordre ref')}: ${formData.orderRef}</p>
                          ${
                            formData.invoiceNo
                              ? `<p>
                                ${t('Fakturanummer')}: ${formData.invoiceNo}
                              </p>`
                              : ''
                          }
                          <p>${t('orders.date')}: ${formatDate(new Date())}</p>
                        </div>
                        <div class="qr-code">
                          <img src="${document.querySelector('canvas')?.toDataURL()}" width="200" />
                        </div>
                      </div>
                      
                      <div>
                        <h2>${t('orders.companyInformation')}</h2>
                        <p>${t('orders.companyName')}: ${formData.companyName}</p>
                      </div>
                      
                      <div>
                        <h2>${t('orders.deliveryInformation')}</h2>
                        <!-- <p>${t('orders.deliveryDate')}: ${
                formData.deliveryDate ? formatDate(formData.deliveryDate) : t('orders.notSpecified')
              }</p> -->
                        <p>${t('orders.deliveryAddress')}: ${
                formData.deliveryAddress || t('orders.notSpecified')
              }, ${formData.deliveryZip} ${formData.deliveryCity}</p>
                        <p>${t('orders.deliveryAtt')}: ${
                formData.deliveryAtt || t('orders.notSpecified')
              }</p>
                        <p>${t('orders.contactPerson')}: ${
                formData.contactPerson || t('orders.notSpecified')
              }</p>
                      </div>
                      
                      <div>
                        <h2>${t('orders.products')}</h2>
                        <table>
                          <thead>
                            <tr>
                              <th>${t('orders.product')}</th>
                              <th>${t('orders.quantity')}</th>
                              <th>${t('orders.unit')}</th>
                              <th>${t('orders.total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${formData.products
                              .map((product) => {
                                // @ts-ignore
                                const unit = productUnitsMap[product.id]?.find(
                                  (p: any) => p.value == product.unit
                                );
                                return `
                              <tr>
                                <td>${product.name}</td>
                                <td>${product.quantity}</td>
                                <td>${unit?.label}</td>
                                <td>${product.quantity * unit?.value} liter</td>
                              </tr>
                            `;
                              })
                              .join('')}
                          </tbody>
                        </table>
                      </div>
                      
                      ${
                        formData.returnedContainers && formData.returnedContainers.length > 0
                          ? `
                      <div>
                        <h2>${t('orders.returnedContainers', 'Retur emballage')}</h2>
                        <table>
                          <thead>
                            <tr>
                              <th>${t('orders.containerType', 'Emballage type')}</th>
                              <th>${t('orders.quantity', 'Antal')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${formData.returnedContainers
                              .map((container) => {
                                const containerType = containerTypes.find(
                                  (c) => c.value === container.type
                                );
                                return `
                              <tr>
                                <td>${containerType?.label || container.type}</td>
                                <td>${container.quantity}</td>
                              </tr>
                            `;
                              })
                              .join('')}
                          </tbody>
                        </table>
                      </div>
                      `
                          : ''
                      }
                      
                      <!-- <div class="footer">
                        <div>
                          <p>${t('orders.deliveredBy')}:</p>
                          <div class="signature-box">${t('orders.signature')}</div>
                        </div>
                        <div>
                          <p>${t('orders.receivedBy')}:</p>
                          <div class="signature-box">${t('orders.signature')}</div>
                        </div>
                      </div> -->
                    </div>
                    <script>
                      window.onload = function() { window.print(); }
                    </script>
                  </body>
                </html>
              `);
              printWindow?.document.close();
            }}
          >
            {t('orders.printDeliveryNote')}
          </Button>
        </div>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-md font-semibold">{t('orders.orderInformation')}</h3>
                <p className="text-sm">
                  {t('orders.orderId')}: {formData.id}
                </p>
                <p className="text-sm">
                  {t('Ordre ref')}: {formData.orderRef}
                </p>
                {formData.invoiceNo && (
                  <p className="text-sm">
                    {t('Fakturanummer')}: {formData.invoiceNo}
                  </p>
                )}
                <p className="text-sm">
                  {t('orders.status')}:{' '}
                  <Badge className={getStatusColor(formData.status)}>
                    {t(translateStatus(formData.status))}
                  </Badge>
                </p>
                <p className="text-sm">
                  {t('orders.createdAt')}: {formatDate(formData.createTime)}
                </p>
              </div>
              <div className="bg-white p-2 rounded border scale(0.5)">
                <QRCodeCanvas
                  style={{ width: 100, height: 100 }}
                  level="H"
                  value={`https://app.aks2tal.com/o/${formData.id}`}
                  size={600}
                />
              </div>
            </div>

            <Separator />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-md font-semibold">{t('orders.companyInformation')}</h3>
                <p className="text-sm">{t('orders.companyName')}:</p>
                <p className="text-sm">{formData.companyName}</p>
              </div>
              <div>
                <h3 className="text-md font-semibold">{t('orders.deliveryInformation')}</h3>
                <p className="text-sm">
                  {t('orders.deliveryDate')}:{' '}
                  {formData.deliveryDate
                    ? formatDate(formData.deliveryDate)
                    : t('orders.notSpecified')}
                </p>
                <p className="text-sm">
                  {t('orders.deliveryAddress')}:{' '}
                  {formData.deliveryAddress || t('orders.notSpecified')}, {formData.deliveryZip}{' '}
                  {formData.deliveryCity}
                </p>
                {/* <p></p> */}
                <p className="text-sm">
                  {t('orders.deliveryAtt')}: {formData.deliveryAtt || t('orders.notSpecified')}
                </p>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-md font-semibold mb-2">{t('orders.products')}</h3>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('orders.product')}</TableHead>
                      <TableHead>{t('orders.quantity')}</TableHead>
                      <TableHead>{t('orders.unit')}</TableHead>
                      <TableHead>{t('orders.total')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                          {t('orders.noProductsAdded')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      formData.products.map((product, index) => {
                        // @ts-ignore
                        const unit = productUnitsMap[product.id]?.find(
                          (p: any) => p.value == product.unit
                        );
                        return (
                          <TableRow key={index}>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.quantity}</TableCell>
                            <TableCell>{unit?.label}</TableCell>
                            <TableCell>{product.quantity * unit?.value} liter</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {formData.returnedContainers && formData.returnedContainers.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="text-md font-semibold mb-2">
                    {t('orders.returnedContainers', 'Retur emballage')}
                  </h3>
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('orders.containerType', 'Emballage type')}</TableHead>
                          <TableHead>{t('orders.quantity', 'Antal')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.returnedContainers.map((container, index) => {
                          const containerType = containerTypes.find(
                            (c) => c.value === container.type
                          );
                          return (
                            <TableRow key={index}>
                              <TableCell>{containerType?.label || container.type}</TableCell>
                              <TableCell>{container.quantity}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            {/* <Separator /> */}

            {/* <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="text-md font-semibold mb-2">{t('orders.deliveredBy')}</h3>
                <div className="h-16 border-t border-dashed pt-1 text-muted-foreground text-sm">
                  {t('orders.signature')}
                </div>
              </div>
              <div>
                <h3 className="text-md font-semibold mb-2">{t('orders.receivedBy')}</h3>
                <div className="h-16 border-t border-dashed pt-1 text-muted-foreground text-sm">
                  {t('orders.signature')}
                </div>
              </div>
            </div> */}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render the appropriate content based on active tab
  const renderContent = () => {
    if (activeTab === 'details') {
      return renderDetailsTab();
    } else if (activeTab === 'deliverynote') {
      return renderDeliveryNoteTab();
    }
    return null;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-4 pb-4 bg-gray-50 border ">
            <DialogTitle>
              {mode === 'edit' ? t('orders.editOrder') : t('orders.orderDetails')}
              <span className="ml-2 text-muted-foreground">#{formData.id}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Ordredetaljer, produkter og leveringsoplysninger.
            </DialogDescription>
          </DialogHeader>
          {formData.productDataIncomplete ? (
            <p role="alert" className="mx-6 text-sm text-amber-800">
              Produktdata er ufuldstændige. Lagring er blokeret for at undgå at overskrive
              produktlinjer, der ikke kunne indlæses. Kontakt en administrator.
            </p>
          ) : null}

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="w-48 border-r bg-muted/40 flex flex-col">
              <div className="flex flex-col p-2 gap-1">
                <Button
                  variant={activeTab === 'details' ? 'secondary' : 'ghost'}
                  className="justify-start"
                  onClick={() => setActiveTab('details')}
                >
                  {t('orders.orderDetails')}
                </Button>
                <Button
                  variant={activeTab === 'deliverynote' ? 'secondary' : 'ghost'}
                  className="justify-start"
                  onClick={() => setActiveTab('deliverynote')}
                >
                  {t('orders.deliveryNote')}
                </Button>
                <OrderDealButton orgId={formData.orgId} />
              </div>

              <div className="mt-auto p-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setHistoryDialogOpen(true)}
                >
                  <History className="mr-2 h-4 w-4" />
                  {t('orders.viewHistory')}
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <ScrollArea className="flex-1 px-6">
                {formSubmitted && Object.keys(errors).length > 0 && (
                  <Alert variant="destructive" className="mb-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {Object.entries(errors).map(([_, error]) => (
                        <div>{t(error)}</div>
                      ))}
                    </AlertDescription>
                  </Alert>
                )}

                {renderContent()}
              </ScrollArea>

              {mode === 'edit' && (
                <DialogFooter className="p-6 pt-2">
                  <Button onClick={handleSave} loading={formSubmitted} disabled={formData.productDataIncomplete}>
                    {t('orders.save')}
                  </Button>
                </DialogFooter>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Separate Order History Dialog */}
      {formData.id && (
        <OrderHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          orderId={formData.id}
          order={formData}
        />
      )}
    </>
  );
}
